/**
 * Ageing Analysis — AR and AP outstanding breakdown by ageing bucket.
 * Supports Customer and Supplier party types, as-at date, and due-date vs document-date ageing.
 * Summary = one row per party; Detail = every open item under its party, in its band column (422169).
 */
import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw, BarChart2, MapPin, Layers, Search, Download, ExternalLink,
} from 'lucide-react';

import { openItemApi }   from '@/api/openItem.api';
import { ageingProfileApi } from '@/api/ageingProfile.api';
import { exportToCsv }   from '@/utils/exportCsv';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { Spinner }       from '@/components/ui/Spinner';
import { cn }            from '@/utils/cn';
import { AppRoutes }     from '@/constants/routes';
import type { RootState } from '@/state/store';
import type { AgeingRowDto, AgeingBucketDto } from '@/types/openItem.types';

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const today = () => new Date().toISOString().slice(0, 10);
const day = (d: string | null) => (d ? d.slice(0, 10) : '');

// The report's bands come from the resolved profile (GAP-011); colour is assigned by position, so a
// ladder of any length still reads cool-to-hot. The final entry is always the row total.
const BAND_COLORS = [
  'text-emerald-700 dark:text-emerald-400',
  'text-blue-700 dark:text-blue-400',
  'text-amber-600 dark:text-amber-400',
  'text-orange-600 dark:text-orange-400',
  'text-red-600 dark:text-red-400',
  'text-red-800 dark:text-red-300',
];
const bandColor = (i: number) => BAND_COLORS[Math.min(i, BAND_COLORS.length - 1)];
const TOTAL_COLOR = 'text-slate-900 dark:text-white font-bold';

interface AgeingPageProps {
  /** Pre-select and lock party type (used by AR/AP dedicated pages) */
  defaultPartyType?: 'Customer' | 'Supplier';
  lockPartyType?:    boolean;
}

export const AgeingPage = ({ defaultPartyType = 'Customer', lockPartyType = false }: AgeingPageProps = {}) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canView = hasPermission(Permissions.FIN_OPEN_ITEM_VIEW);
  const canViewAllLoc = hasPermission(Permissions.ALLOCATION_VIEW_ALL_LOCATIONS) || hasPermission(Permissions.TXN_VIEW_ALL_LOCATIONS);
  const canViewAllCC  = hasPermission(Permissions.ALLOCATION_VIEW_ALL_COST_CENTERS) || hasPermission(Permissions.TXN_VIEW_ALL_COST_CENTERS);

  const session = useSelector((s: RootState) => s.session.session);

  // Whole company or the session's location / cost centre. "All" is offered only to users who may
  // see every location (the server enforces the same), and is their default: an open item with no
  // location (e.g. an integrated bill) would otherwise vanish from a scoped report.
  const [allLoc, setAllLoc] = useState(canViewAllLoc);
  const [allCC,  setAllCC]  = useState(canViewAllCC);

  const [partyType,  setPartyType]  = useState<'Customer' | 'Supplier'>(defaultPartyType);
  const [asAtDate,   setAsAtDate]   = useState(today());
  const [useDueDate, setUseDueDate] = useState(true);
  const [currency,   setCurrency]   = useState('');
  const [partySearch, setPartySearch] = useState('');
  const [view,       setView]       = useState<'Summary' | 'Detail'>('Summary');

  const [rows,    setRows]    = useState<AgeingRowDto[]>([]);
  const [buckets, setBuckets] = useState<AgeingBucketDto[]>([]);
  const [profileName, setProfileName] = useState<string>('');

  // ── Ageing profile (GAP-011 AC2) ──────────────────────────────────────────
  // '' means "the company default for this party type", which is what the server picks when no
  // profile is named. Kept distinct from a chosen profile that happens to BE the default, so the
  // report header can say which of the two the reader is looking at.
  const [profiles,  setProfiles]  = useState<{ id: string; profileName: string; isDefault: boolean }[]>([]);
  const [profileId, setProfileId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loaded,  setLoaded]  = useState(false);

  // Debtors and creditors age on different terms, so the list is per party type — and a profile
  // selected for one must not survive a switch to the other.
  useEffect(() => {
    if (!canView) return;
    setProfileId('');
    ageingProfileApi.list(partyType === 'Customer' ? 'CUSTOMER' : 'SUPPLIER')
      .then(res => setProfiles(
        (res.data.data ?? [])
          .filter(p => p.isActive)
          .map(p => ({ id: p.id, profileName: p.profileName, isDefault: p.isDefault }))))
      .catch(() => setProfiles([]));   // the picker simply does not appear
  }, [canView, partyType]);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const res = await openItemApi.getAgeing({
        partyType,
        asAtDate,
        useDueDate,
        currencyCode: currency || undefined,
        locationId:   allLoc ? undefined : session?.locationId   || undefined,
        costCenterId: allCC  ? undefined : session?.costCenterId || undefined,
        profileId:    profileId || undefined,
        // Always fetched, so Summary/Detail switch instantly and show the same run.
        includeLines: true,
      });
      const report = res.data?.data;
      setBuckets(report?.buckets ?? []);
      setRows(report?.rows ?? []);
      setProfileName(report?.profileName ?? '');
      setLoaded(true);
    } catch (e) { console.error('Ageing load failed', e); }
    finally { setLoading(false); }
  }, [canView, partyType, asAtDate, useDueDate, currency, session, profileId, allLoc, allCC]);

  // Filter rows by party search (client-side)
  const filteredRows = partySearch.trim()
    ? rows.filter(r =>
        r.partyCode.toLowerCase().includes(partySearch.toLowerCase()) ||
        r.partyName.toLowerCase().includes(partySearch.toLowerCase())
      )
    : rows;

  // Column totals — one per band, plus the grand total.
  const bandTotals = buckets.map((_, i) =>
    filteredRows.reduce((s, r) => s + (r.bucketAmounts[i] ?? 0), 0));
  const grandTotal = filteredRows.reduce((s, r) => s + r.totalOutstanding, 0);

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        <p className="text-sm">You do not have permission to view ageing analysis.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Ageing Analysis</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {loaded
                ? `${filteredRows.length} ${partyType === 'Customer' ? 'customer' : 'supplier'}${filteredRows.length !== 1 ? 's' : ''}${profileName ? ` · ${profileName} bands` : ''}`
                : 'Configure and run ageing'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {loaded && filteredRows.length > 0 && view === 'Detail' && (
              <button
                onClick={() => exportToCsv(`ageing-detail-${partyType.toLowerCase()}-${asAtDate}.csv`,
                  filteredRows.flatMap(r => (r.lines ?? []).map(l => ({
                    Party: r.partyName, Code: r.partyCode, Currency: r.currencyCode,
                    Type: l.documentType, Document: l.documentNumber, 'Their Ref': l.externalDocumentNumber ?? '',
                    'Doc Date': day(l.documentDate), 'Due Date': day(l.dueDate), Days: l.days,
                    Original: l.originalAmount,
                    ...Object.fromEntries(buckets.map((b, i) => [b.label, i === l.bucketIndex ? l.outstandingAmount : 0])),
                    Outstanding: l.outstandingAmount,
                    'Ageing Bands': profileName || 'Standard',
                    'Aged By': useDueDate ? 'Due date' : 'Document date',
                    'As At': asAtDate,
                  })))
                )}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-600 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-300 rounded-lg transition-colors"
                title="Export to CSV">
                <Download size={13} /> Export
              </button>
            )}
            {loaded && filteredRows.length > 0 && view === 'Summary' && (
              <button
                onClick={() => exportToCsv(`ageing-${partyType.toLowerCase()}-${asAtDate}.csv`,
                  filteredRows.map(r => ({
                    Party: r.partyName, Code: r.partyCode, Currency: r.currencyCode,
                    ...Object.fromEntries(buckets.map((b, i) => [b.label, r.bucketAmounts[i] ?? 0])),
                    Total: r.totalOutstanding,
                    // GAP-011 AC4. The band columns are meaningless without the ladder that
                    // produced them: two exports with the same headings can come from different
                    // profiles, and a file that does not say which is not evidence of anything.
                    'Ageing Bands': profileName || 'Standard',
                    'Aged By': useDueDate ? 'Due date' : 'Document date',
                    'As At': asAtDate,
                  }))
                )}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-600 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-300 rounded-lg transition-colors"
                title="Export to CSV">
                <Download size={13} /> Export
              </button>
            )}
            <button onClick={() => void load()} disabled={loading} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 transition-colors disabled:opacity-40" title="Refresh">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Scope chips — a toggle between the whole company and the session's own, for users who may see all */}
        {(session?.locationName || session?.costCenterName) && (
          <div className="flex items-center gap-2 mt-3">
            <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider flex items-center gap-1"><MapPin size={10} /> Scope:</span>
            {session?.locationName && (
              <ScopeChip icon={<MapPin size={10} />} canToggle={canViewAllLoc} all={allLoc}
                allLabel="All locations" ownLabel={`${session.locationCode} — ${session.locationName}`}
                onToggle={() => { setAllLoc(v => !v); setLoaded(false); }} />
            )}
            {session?.costCenterName && (
              <ScopeChip icon={<Layers size={10} />} canToggle={canViewAllCC} all={allCC}
                allLabel="All cost centres" ownLabel={`${session.costCenterCode} — ${session.costCenterName}`}
                onToggle={() => { setAllCC(v => !v); setLoaded(false); }} />
            )}
          </div>
        )}

        {/* Controls */}
        <div className="flex items-center gap-4 mt-4 flex-wrap">
          {/* Party type */}
          {lockPartyType ? (
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              {partyType} ageing
            </span>
          ) : (
            <div className="flex items-center rounded-lg overflow-hidden ring-1 ring-slate-200 dark:ring-slate-600">
              {(['Customer', 'Supplier'] as const).map(pt => (
                <button key={pt} onClick={() => { setPartyType(pt); setLoaded(false); }}
                  className={cn('px-4 py-1.5 text-sm font-medium transition-colors', partyType === pt ? 'bg-blue-600 text-white' : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50')}>
                  {pt}s
                </button>
              ))}
            </div>
          )}

          {/* As-at date */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500 whitespace-nowrap">As at:</label>
            <input type="date" value={asAtDate} onChange={e => setAsAtDate(e.target.value)}
              className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-1.5 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>

          {/* Ageing basis */}
          <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer">
            <input type="checkbox" checked={useDueDate} onChange={e => setUseDueDate(e.target.checked)} className="accent-blue-600" />
            Age by due date
          </label>

          {/* Ageing profile (GAP-011) */}
          {profiles.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500 whitespace-nowrap">Ageing bands:</label>
              <select
                value={profileId}
                onChange={e => setProfileId(e.target.value)}
                className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-1.5
                           bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 outline-none"
              >
                <option value="">Company default</option>
                {profiles.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.profileName}{p.isDefault ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Currency */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500 whitespace-nowrap">Currency:</label>
            <input value={currency} onChange={e => setCurrency(e.target.value.toUpperCase())} placeholder="All"
              className="w-16 text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1.5 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none font-mono uppercase text-center" />
          </div>

          <button onClick={() => void load()} disabled={loading}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
            <BarChart2 size={14} /> Run Ageing
          </button>

          {/* Report layout */}
          <div className="flex items-center rounded-lg overflow-hidden ring-1 ring-slate-200 dark:ring-slate-600">
            {(['Summary', 'Detail'] as const).map(v => (
              <button key={v} onClick={() => setView(v)}
                className={cn('px-4 py-1.5 text-sm font-medium transition-colors', view === v ? 'bg-blue-600 text-white' : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50')}>
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* Party search (post-load) */}
        {loaded && (
          <div className="relative mt-3 max-w-xs">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={partySearch} onChange={e => setPartySearch(e.target.value)} placeholder="Filter by party…"
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center h-48"><Spinner size="md" /></div>
        ) : !loaded ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400">
            <BarChart2 size={40} className="mb-3 opacity-30" />
            <p className="text-sm font-medium">Configure parameters and click Run Ageing</p>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400">
            <BarChart2 size={40} className="mb-3 opacity-30" />
            <p className="text-sm font-medium">No ageing data found</p>
          </div>
        ) : view === 'Detail' ? (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-700/60 border-b border-slate-200 dark:border-slate-700 text-xs text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-3 text-left font-semibold min-w-[160px]">Document</th>
                  <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">Doc Date</th>
                  <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">Due Date</th>
                  <th className="px-4 py-3 text-right font-semibold">Days</th>
                  <th className="px-4 py-3 text-right font-semibold">Original</th>
                  {buckets.map(b => (
                    <th key={b.sequence} className="px-4 py-3 text-right font-semibold whitespace-nowrap">{b.label}</th>
                  ))}
                  <th className="px-4 py-3 text-right font-semibold whitespace-nowrap">Outstanding</th>
                </tr>
              </thead>
              {filteredRows.map((row, i) => (
                <tbody key={`${row.partyId}-${row.currencyCode}-${i}`} className="divide-y divide-slate-100 dark:divide-slate-700">
                  <tr className="bg-slate-50/60 dark:bg-slate-700/20">
                    <td colSpan={6 + buckets.length} className="px-4 py-2">
                      <span className="font-semibold text-slate-900 dark:text-white text-xs">{row.partyName}</span>
                      <span className="ml-2 text-[11px] text-slate-400">{row.partyCode} · {row.currencyCode}</span>
                    </td>
                  </tr>
                  {(row.lines ?? []).map(l => (
                    <tr key={l.openItemId} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                      <td className="px-4 py-2 pl-8">
                        <div className="font-mono text-xs text-slate-800 dark:text-slate-200">{l.documentNumber}</div>
                        <div className="text-[11px] text-slate-400">{l.documentType}{l.externalDocumentNumber ? ` · ${l.externalDocumentNumber}` : ''}</div>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-500 whitespace-nowrap">{day(l.documentDate)}</td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-500 whitespace-nowrap">{day(l.dueDate) || '—'}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs text-slate-500">{l.days}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs text-slate-500 whitespace-nowrap">{fmt(l.originalAmount)}</td>
                      {buckets.map((b, bi) => (
                        <td key={b.sequence} className={cn('px-4 py-2 text-right font-mono text-xs whitespace-nowrap', bi === l.bucketIndex ? bandColor(bi) : 'text-slate-300 dark:text-slate-600')}>
                          {bi === l.bucketIndex ? fmt(l.outstandingAmount) : '—'}
                        </td>
                      ))}
                      <td className="px-4 py-2 text-right font-mono text-xs whitespace-nowrap text-slate-700 dark:text-slate-200">{fmt(l.outstandingAmount)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-slate-200 dark:border-slate-600">
                    <td colSpan={5} className="px-4 py-2 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                      {row.partyCode} total
                    </td>
                    {buckets.map((b, bi) => (
                      <td key={b.sequence} className={cn('px-4 py-2 text-right font-mono text-xs font-semibold whitespace-nowrap', bandColor(bi))}>
                        {fmt(row.bucketAmounts[bi] ?? 0)}
                      </td>
                    ))}
                    <td className={cn('px-4 py-2 text-right font-mono text-xs whitespace-nowrap', TOTAL_COLOR)}>{fmt(row.totalOutstanding)}</td>
                  </tr>
                </tbody>
              ))}
              <tfoot>
                <tr className="border-t-2 border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/40">
                  <td colSpan={5} className="px-4 py-3">
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Grand Total</span>
                  </td>
                  {buckets.map((b, bi) => (
                    <td key={b.sequence} className={cn('px-4 py-3 text-right font-mono text-sm font-bold whitespace-nowrap', bandColor(bi))}>
                      {fmt(bandTotals[bi] ?? 0)}
                    </td>
                  ))}
                  <td className={cn('px-4 py-3 text-right font-mono text-sm font-bold whitespace-nowrap', TOTAL_COLOR)}>{fmt(grandTotal)}</td>
                </tr>
              </tfoot>
            </table>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden">
            {/* The dynamic band columns can outgrow the viewport (a custom profile can define as
               many bands as it likes) — this is the scroll container. w-full on the table alone
               forced it to the wrapper's width and the outer overflow-hidden then clipped the
               extra columns instead of scrolling to them. */}
            <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-700/60 border-b border-slate-200 dark:border-slate-700 text-xs text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-3 text-left font-semibold sticky left-0 bg-slate-50 dark:bg-slate-700/60 z-10 min-w-[200px]">
                    {partyType === 'Customer' ? 'Customer' : 'Supplier'}
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">Currency</th>
                  {buckets.map(b => (
                    <th key={b.sequence} className="px-4 py-3 text-right font-semibold whitespace-nowrap">{b.label}</th>
                  ))}
                  <th className="px-4 py-3 text-right font-semibold whitespace-nowrap">Total</th>
                  <th className="px-3 py-3 print:hidden" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {filteredRows.map((row, i) => (
                  <tr key={`${row.partyId}-${row.currencyCode}-${i}`}
                    className="group hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                    <td className="px-4 py-3 sticky left-0 bg-white dark:bg-slate-800 z-10">
                      <div className="font-medium text-slate-900 dark:text-white text-xs">{row.partyName}</div>
                      <div className="text-[11px] text-slate-400">{row.partyCode}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{row.currencyCode}</td>
                    {buckets.map((b, bi) => {
                      const val = row.bucketAmounts[bi] ?? 0;
                      return (
                        <td key={b.sequence} className={cn('px-4 py-3 text-right font-mono text-xs whitespace-nowrap', val !== 0 ? bandColor(bi) : 'text-slate-300 dark:text-slate-600')}>
                          {val !== 0 ? fmt(val) : '—'}
                        </td>
                      );
                    })}
                    <td className={cn('px-4 py-3 text-right font-mono text-xs whitespace-nowrap', TOTAL_COLOR)}>
                      {fmt(row.totalOutstanding)}
                    </td>
                    <td className="px-3 py-3 print:hidden">
                      <button
                        onClick={() => navigate(`${AppRoutes.FIN_OPEN_ITEMS}?partyType=${partyType}&partyId=${row.partyId}`)}
                        className="opacity-0 group-hover:opacity-100 text-brand-500 hover:text-brand-700"
                        title={`View ${row.partyName}'s open items — the invoices behind this total`}
                      >
                        <ExternalLink size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Totals row */}
              <tfoot>
                <tr className="border-t-2 border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/40">
                  <td className="px-4 py-3 sticky left-0 bg-slate-50 dark:bg-slate-700/40 z-10">
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Total</span>
                  </td>
                  <td />
                  {buckets.map((b, bi) => (
                    <td key={b.sequence} className={cn('px-4 py-3 text-right font-mono text-sm font-bold whitespace-nowrap', bandColor(bi))}>
                      {fmt(bandTotals[bi] ?? 0)}
                    </td>
                  ))}
                  <td className={cn('px-4 py-3 text-right font-mono text-sm font-bold whitespace-nowrap', TOTAL_COLOR)}>
                    {fmt(grandTotal)}
                  </td>
                  <td className="print:hidden" />
                </tr>
              </tfoot>
            </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/** Scope pill: fixed for a restricted user, a click-to-switch toggle for one who may see everything. */
const ScopeChip = ({ icon, canToggle, all, allLabel, ownLabel, onToggle }: {
  icon: React.ReactNode; canToggle: boolean; all: boolean; allLabel: string; ownLabel: string; onToggle: () => void;
}) => {
  const cls = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium';
  if (!canToggle)
    return <span className={cn(cls, 'bg-blue-50 text-blue-700 ring-1 ring-blue-200')}>{icon}{ownLabel}</span>;
  return (
    <button type="button" onClick={onToggle} title="Click to switch between the whole company and your own"
      className={cn(cls, 'ring-1 transition-colors hover:ring-blue-300',
        all ? 'bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:ring-slate-600'
            : 'bg-blue-50 text-blue-700 ring-blue-200')}>
      {icon}{all ? allLabel : ownLabel}
    </button>
  );
};
